import { useCallback, useEffect } from 'react';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useTopBarCustomEventListener } from '@frontend/shared';
import { useTaskTrayShallowStore } from '@frontend/task-tray';
import { useTopBarShallowStore, type TopBarSelectableIcons } from '../store/use-top-bar-store';

export const useTopBarSelection = () => {
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasOnboardingTaskFeatureFlag = getFeatureFlagValue('portal-onboarding-tasks');
  const { isTaskTrayOpen } = useTaskTrayShallowStore('isTaskTrayOpen', 'toggleTaskTrayOpen');
  const {
    selectedIcon,
    isActionMobileOpen,
    setSelectedIcon,
    setActionMobileOpen,
    setSchedulePulseOpen,
    setQuickFillOpen,
  } = useTopBarShallowStore(
    'selectedIcon',
    'isActionMobileOpen',
    'setSelectedIcon',
    'setActionMobileOpen',
    'setSchedulePulseOpen',
    'setQuickFillOpen'
  );
  const isTaskTrayIconButtonVisible = false && hasOnboardingTaskFeatureFlag;

  // respond to external event to open certain trays
  useTopBarCustomEventListener('set-selected-top-bar-tray', ({ icon, mouseEvent }) => {
    mouseEvent?.stopPropagation?.();
    if (icon === 'schedulePulse' || icon === 'quickfill') {
      setSelectedIcon(icon as TopBarSelectableIcons);
      if (icon === 'schedulePulse') {
        setSchedulePulseOpen(true);
      } else if (icon === 'quickfill') {
        setQuickFillOpen(true);
      }
    }
  });

  // keep selection in sync with task tray visibility (responsive changes)
  useEffect(() => {
    if (isTaskTrayIconButtonVisible && isTaskTrayOpen && !selectedIcon) {
      setSelectedIcon('taskTray');
    } else if (!isTaskTrayIconButtonVisible && selectedIcon === 'taskTray') {
      setSelectedIcon('');
    } else if (isTaskTrayIconButtonVisible && !isTaskTrayOpen && selectedIcon === 'taskTray') {
      setSelectedIcon('');
    }
  }, [isTaskTrayIconButtonVisible, isTaskTrayOpen, selectedIcon, setSelectedIcon]);

  // action menu open state synchronization
  useEffect(() => {
    if (isActionMobileOpen && selectedIcon !== 'actions') setSelectedIcon('actions');
    else if (!isActionMobileOpen && selectedIcon === 'actions') setSelectedIcon('');
  }, [isActionMobileOpen, selectedIcon, setSelectedIcon]);

  const closeAll = useCallback(() => {
    setSelectedIcon('');
  }, [setSelectedIcon]);

  return {
    isActionMobileOpen,
    setActionMobileOpen,
    closeAll,
  };
};
